/**
 * TrendCharts - Visualizes monthly trends comparing current and previous year
 */

import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { SIEProcessedData } from './types';
import { formatAmountWithDisplayFormat, determineOptimalUnit, MONTH_NAMES, type DisplayUnit, type DisplayFormat } from './utils';

interface TrendChartsProps {
  currentYear: SIEProcessedData;
  previousYear: SIEProcessedData;
  displayFormat?: DisplayFormat;
}

export function TrendCharts({ currentYear, previousYear, displayFormat = 'auto' }: TrendChartsProps) {
  const currentFiscalYear = currentYear.metadata.fiscalYears.find(fy => fy.index === 0);
  const previousFiscalYear = previousYear.metadata.fiscalYears.find(fy => fy.index === 0);

  const currentYearLabel = currentFiscalYear?.end?.slice(0, 4) || 'Innevarande';
  const previousYearLabel = previousFiscalYear?.end?.slice(0, 4) || 'Föregående';

  // Prepare monthly trend data
  const monthlyTrendData = useMemo(() => {
    const currByMonth = currentYear.aggregated.incomeStatementByMonth || {};
    const prevByMonth = previousYear.aggregated.incomeStatementByMonth || {};

    // Get all unique months (just the month part, like '01', '02', etc.)
    const allMonths = new Set<string>();
    Object.keys(currByMonth).forEach(m => allMonths.add(m.slice(5, 7)));
    Object.keys(prevByMonth).forEach(m => allMonths.add(m.slice(5, 7)));

    // Sort months
    const sortedMonths = Array.from(allMonths).sort();

    return sortedMonths.map(monthNum => {
      // Find matching data for each year
      const currKey = Object.keys(currByMonth).find(k => k.slice(5, 7) === monthNum);
      const prevKey = Object.keys(prevByMonth).find(k => k.slice(5, 7) === monthNum);

      const currData = currKey ? currByMonth[currKey] : null;
      const prevData = prevKey ? prevByMonth[prevKey] : null;

      return {
        month: MONTH_NAMES[monthNum] || monthNum,
        currentRevenue: currData?.revenue || 0,
        previousRevenue: prevData?.revenue || 0,
        currentExpenses: currData?.expenses || 0,
        previousExpenses: prevData?.expenses || 0,
        currentResult: currData?.netResult || 0,
        previousResult: prevData?.netResult || 0,
      };
    });
  }, [currentYear, previousYear]);

  // Determine optimal display unit based on all values in the charts
  const displayUnit: DisplayUnit = useMemo(() => {
    const allAmounts = monthlyTrendData.flatMap(d => [
      d.currentRevenue,
      d.previousRevenue,
      d.currentExpenses,
      d.previousExpenses,
      d.currentResult,
      d.previousResult,
    ]);
    return determineOptimalUnit(allAmounts);
  }, [monthlyTrendData]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border rounded-lg shadow-lg p-3">
          <p className="font-medium mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {formatAmountWithDisplayFormat(entry.value, displayFormat, displayUnit)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Formatter for Y-axis labels
  const yAxisFormatter = (v: number) => formatAmountWithDisplayFormat(v, displayFormat, displayUnit);

  return (
    <div className="space-y-6">
      {/* Revenue Trend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Intäktsutveckling</CardTitle>
          <CardDescription>Månadsvis jämförelse av intäkter</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyTrendData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis dataKey="month" className="text-xs" />
              <YAxis 
                tickFormatter={yAxisFormatter}
                className="text-xs"
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line
                type="monotone"
                dataKey="currentRevenue"
                name={`Intäkter ${currentYearLabel}`}
                stroke="hsl(var(--success))"
                strokeWidth={2}
                dot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="previousRevenue"
                name={`Intäkter ${previousYearLabel}`}
                stroke="hsl(var(--success))"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ r: 3 }}
                opacity={0.6}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Expenses Trend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Kostnadsutveckling</CardTitle>
          <CardDescription>Månadsvis jämförelse av kostnader</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyTrendData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis dataKey="month" className="text-xs" />
              <YAxis 
                tickFormatter={yAxisFormatter}
                className="text-xs"
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line
                type="monotone"
                dataKey="currentExpenses"
                name={`Kostnader ${currentYearLabel}`}
                stroke="hsl(var(--destructive))"
                strokeWidth={2}
                dot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="previousExpenses"
                name={`Kostnader ${previousYearLabel}`}
                stroke="hsl(var(--destructive))"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ r: 3 }}
                opacity={0.6}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Result Comparison - Bar Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Resultatutveckling</CardTitle>
          <CardDescription>Månadsvis jämförelse av resultat</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlyTrendData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis dataKey="month" className="text-xs" />
              <YAxis 
                tickFormatter={yAxisFormatter}
                className="text-xs"
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar
                dataKey="currentResult"
                name={`Resultat ${currentYearLabel}`}
                fill="hsl(var(--primary))"
              />
              <Bar
                dataKey="previousResult"
                name={`Resultat ${previousYearLabel}`}
                fill="hsl(var(--primary))"
                opacity={0.4}
              />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
